import { getListOfEndChapters, getListOfStartChapters } from '../Contexts/DiffitBooksContext';
import { Dropdown } from '../Components/Dropdown';
import { SearchBooksCombobox } from '../Components/SearchBooksCombobox';
import { BOOKS_ID, usePacketInput } from '../Contexts/PacketInputContext';
import { WithTooltip } from '../../../components/WithTooltip';
import { useMemo, useCallback } from 'react';

export const BooksInput = () => {
  const { inputType } = usePacketInput();
  if (inputType.id !== BOOKS_ID) return null;

  return (
    <>
      <SearchBooksCombobox />
      <div className='flex flex-col sm:flex-row gap-y-3 gap-x-3 sm:mb-2'>
        <StartChapterDropdown />
        <EndChapterDropdown />
      </div>
    </>
  );
};

const chapterDropdownClasses =
  'sm:min-w-44 sm:max-w-[300px] w-fit bg-gray-50 hover:bg-gray-100 text-gray-900 disabled:text-gray-500 outline outline-1 outline-offset-[-1px] outline-gray-100';

const StartChapterDropdown = () => {
  const { book, startChapter, setStartChapter, endChapter, setEndChapter } = usePacketInput();

  // Memoize options for performance
  const options = useMemo(
    () =>
      book
        ? getListOfStartChapters(book.chapters, endChapter?.value ? endChapter.value - 1 : -1)
        : ['Select a chapter'],
    [book, endChapter]
  );

  // Memoize handler
  const handleSetStartChapter = useCallback(
    (chapter) => {
      if (startChapter === -1) {
        // first time setting start chapter, also set end chapter to the same value
        setEndChapter(chapter);
      }
      setStartChapter(chapter);
    },
    [setStartChapter, setEndChapter, startChapter]
  );

  const dropdown = (
    <Dropdown
      label='Start Chapter'
      name='Start Chapter'
      optionList={options}
      selected={
        !book ? 'Select a chapter' : startChapter === -1 ? 'Select a chapter' : startChapter
      }
      setSelected={handleSetStartChapter}
      isDisabled={!book}
      extraClasses={chapterDropdownClasses}
      premiumRow={{
        show: true,
        message: 'Get unlimited access',
        link: 'https://web.diffit.me/plans',
        type: 'premium',
        location: 'Start Chapter Dropdown',
      }}
    />
  );

  return (
    <WithTooltip
      text={!book ? 'Select a book to choose chapters' : undefined}
      position='top'
      anchor='center'
      showArrow={true}>
      {dropdown}
    </WithTooltip>
  );
};

const EndChapterDropdown = () => {
  const { book, startChapter, endChapter, setEndChapter } = usePacketInput();

  // Memoize options for performance
  const options = useMemo(
    () =>
      book
        ? getListOfEndChapters(book.chapters, startChapter?.value ? startChapter.value - 1 : -1)
        : ['Select a chapter'],
    [book, startChapter]
  );

  // Memoize handler
  const handleSetEndChapter = useCallback(
    (chapter) => {
      setEndChapter(chapter);
    },
    [setEndChapter]
  );

  const dropdown = (
    <Dropdown
      label='End Chapter'
      name='End Chapter'
      optionList={options}
      selected={!book ? 'Select a chapter' : endChapter === -1 ? 'Select a chapter' : endChapter}
      setSelected={handleSetEndChapter}
      isDisabled={!book}
      extraClasses={chapterDropdownClasses}
      premiumRow={{
        show: true,
        message: 'Get unlimited access',
        link: 'https://web.diffit.me/plans',
        type: 'premium',
        location: 'End Chapter Dropdown',
      }}
    />
  );

  return (
    <WithTooltip
      text={!book ? 'Select a book to choose chapters' : undefined}
      position='top'
      anchor='center'
      showArrow={true}>
      {dropdown}
    </WithTooltip>
  );
};
